import type { JSX } from 'react';
import { Dnd5eSheetAdapter } from '@roll1/shared/systems/dnd5e/adapters/hud-adapter.js';
import type { HudCharacterData } from '../../../lib/character-hud-store';
import type { TranslationKeys } from '../../../i18n/types.js';
import { FullSheetOverlay } from '../../character-hud/FullSheetOverlay';
import { useAiDrafts } from '../../../hooks/useAiDrafts';
import type { CharacterSheet } from '@roll1/shared';
import { SheetManageBlock, type SheetManageData } from './SheetManageBlock';
import { SectionHeader } from '../../ui/section-header';
import { Pill } from '../../ui/pill';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useEffect, useState } from 'react';

/**
 * Pane-уровень листа в Studio — СОБСТВЕННАЯ компактная карточка по макету
 * (docs/backlog/gm-studio-mockup.html): статлайн КД/ХП/скорость, мини-грид
 * характеристик, черты, инвентарь. Ранее pane собирался из секций
 * Character-таба — они принесли чужой визуальный язык HUD (сериф, гигантские
 * цифры, Identity с полом/ростом/весом; census U2) и на живом стенде выглядели
 * «вообще не как задумывали». Секции остаются уровню full (⤢ — FullSheetOverlay
 * с полным редактированием); pane — компактный обзор для рабочего набора бита.
 */

type AdapterArgs = Parameters<typeof Dnd5eSheetAdapter.fromSheet>;

export interface SheetBacklink {
  readonly id: string;
  readonly name: string;
}

export interface SheetEntityViewProps {
  sheetId: string;
  level: 'pane' | 'full';
  sheet: unknown;
  template: unknown;
  role?: string;
  spellMap?: unknown;
  equipmentMap?: unknown;
  send?: (msg: unknown) => void;
  onPatch?: (patch: unknown) => void;
  onClose?: () => void;
  /** «Упоминается в» — журнальные записи, привязанные к листу (макет: .backlinks). */
  backlinks?: readonly SheetBacklink[];
  onOpenBacklink?: (entryId: string) => void;
  /** T-19: owner / group / public fields / delete. Given by the Studio surface only (a peek card has none). */
  manage?: SheetManageData;
  t?: (key: keyof TranslationKeys) => string;
}

const STAT_ORDER = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const;
type StatId = (typeof STAT_ORDER)[number];

const STAT_KEY: Record<StatId, keyof TranslationKeys> = {
  str: 'characterMode.stat.str',
  dex: 'characterMode.stat.dex',
  con: 'characterMode.stat.con',
  int: 'characterMode.stat.int',
  wis: 'characterMode.stat.wis',
  cha: 'characterMode.stat.cha',
};

const fmtMod = (score: number): string => {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
};

const fallbackT = (key: keyof TranslationKeys): string => key;

/**
 * GJ-14: a number of the pane that the GM sets while preparing (hit points, armour class). It reads as text («20 / 20»,
 * as before) and becomes a field on a click; it is written when the field is left or Enter is pressed — the same
 * direct sheet:update the full sheet's «correction» fields send. Escape drops the edit.
 */
function NumberCell(props: { readonly value: number; readonly label: string; readonly min?: number; readonly onCommit: (n: number) => void }): JSX.Element {
  const { value, label, min = 0, onCommit } = props;
  const [editing, setEditing] = useState<boolean>(false);
  const [text, setText] = useState<string>(String(value));
  useEffect(() => { if (!editing) setText(String(value)); }, [value, editing]);
  const commit = (): void => {
    setEditing(false);
    const n = Number.parseInt(text, 10);
    if (!Number.isFinite(n)) return;
    const next = Math.max(min, n);
    if (next !== value) onCommit(next);
  };
  if (!editing) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="inline"
        className="px-1 font-mono text-body tabular-nums text-fg hover:bg-line/50"
        title={label}
        aria-label={`${label}: ${value}`}
        data-testid="number-cell"
        onClick={(): void => { setText(String(value)); setEditing(true); }}
      >
        {value}
      </Button>
    );
  }
  return (
    <Input
      type="number"
      inputMode="numeric"
      autoFocus
      value={text}
      aria-label={label}
      className="h-6 w-14 px-1 text-center font-mono text-body tabular-nums"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
        if (e.key === 'Escape') { e.preventDefault(); setText(String(value)); setEditing(false); }
      }}
    />
  );
}

export function SheetEntityView(props: SheetEntityViewProps): JSX.Element {
  const {
    sheetId,
    level,
    sheet,
    template,
    role,
    spellMap,
    equipmentMap,
    send,
    onPatch,
    onClose,
    backlinks = [],
    onOpenBacklink,
    manage,
    t = fallbackT,
  } = props;

  const { drafts } = useAiDrafts();
  const sheetDrafts = Array.isArray(drafts)
    ? drafts.filter(
        (draft: { entityRef?: { kind?: string; id?: string }; status?: string }) =>
          draft.entityRef?.kind === 'sheet' &&
          draft.entityRef?.id === sheetId &&
          draft.status === 'pending',
      )
    : [];
  const hasSheetDraft = sheetDrafts.length > 0;

  const adapted = Dnd5eSheetAdapter.fromSheet(
    ...([sheet, template, role, spellMap, equipmentMap] as AdapterArgs),
  );
  const data = adapted.data as HudCharacterData;

  if (level === 'full') {
    const overlay = (
      <FullSheetOverlay
        open={true}
        embedded={true}
        adaptedData={data}
        onPatch={onPatch}
        send={send}
        sheetId={sheetId}
        onClose={() => onClose?.()}
      />
    );
    if (manage === undefined || send === undefined) return overlay;
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex-none p-3 pb-0">
          <SheetManageBlock sheet={sheet as CharacterSheet} manage={manage} send={send} t={t} />
        </div>
        <div className="min-h-0 flex-1">{overlay}</div>
      </div>
    );
  }

  const id = data.identity;
  const features = data.features.slice(0, 4);
  const items = data.inventory.slice(0, 6);

  return (
    <div className="sheet-entity-view flex flex-col gap-3 px-3 pb-4 pt-1 text-ui" data-sheet-id={sheetId}>
      {/* The card's header (group T): one 40 px line — portrait, name, the chips that have something to say. */}
      <div className="pane-head-min flex items-center gap-2.5" data-pane-head="">
        {id.portrait !== undefined && id.portrait !== '' ? (
          <img
            src={id.portrait}
            alt={id.name}
            className="h-8 w-8 shrink-0 rounded-md border border-line object-cover"
          />
        ) : (
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-line bg-gradient-to-br from-line to-panel-2 text-title font-semibold text-hl">
            {id.name.charAt(0) || '?'}
          </div>
        )}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="truncate text-h1 font-semibold leading-tight text-fg">{id.name}</h3>
          <div className="flex flex-wrap gap-1.5">
            {/* An empty chip said nothing (a sheet without race / class showed two empty circles). */}
            {id.race !== '' ? <Pill tone="plain">{id.race}</Pill> : null}
            {id.className !== '' ? <Pill tone="plain">{id.className} {id.level}</Pill> : null}
            {id.background !== undefined && id.background !== '' ? (
              <Pill tone="plain">{id.background}</Pill>
            ) : null}
          </div>
        </div>
      </div>

      {/* Статлайн: КД / ХП / скорость */}
      <div className="grid grid-cols-3 gap-2">
        {(
          [
            ['studio.peek.ac', String(data.ac)],
            ['studio.peek.hp', `${data.hp.current} / ${data.hp.max}`],
            ['studio.peek.speed', `${data.speed}`],
          ] as const
        ).map(([key, value]) => (
          <div
            key={key}
            className={
              hasSheetDraft
                ? 'relative rounded-md border border-dashed border-hl/70 bg-surface/60 px-2 py-1.5 text-center'
                : 'rounded-md border border-line bg-panel-2 px-2.5 py-2 text-center'
            }
          >
            {hasSheetDraft ? (
              <span className="absolute -top-1.5 right-1 rounded-full border border-hl/70 bg-surface px-1 py-px text-label font-semibold uppercase tracking-wide text-hl">
                {t('studio.ai.badgeReview')}
              </span>
            ) : null}
            <div className="text-label font-semibold uppercase tracking-label text-dim">
              {t(key)}
            </div>
            {send !== undefined && manage !== undefined && key === 'studio.peek.ac' ? (
              <div className="mt-0.5 flex justify-center" data-testid="sheet-edit-ac">
                <NumberCell
                  value={data.ac}
                  label={t('studio.peek.ac')}
                  onCommit={(n) => send({ type: 'sheet:update', sheetId, changes: { ac: n } })}
                />
              </div>
            ) : send !== undefined && manage !== undefined && key === 'studio.peek.hp' ? (
              <div className="mt-0.5 flex items-center justify-center gap-1" data-testid="sheet-edit-hp">
                <NumberCell
                  value={data.hp.current}
                  label={t('studio.peek.hp')}
                  onCommit={(n) => send({ type: 'sheet:update', sheetId, changes: data.hp.max <= 0 ? { hp: n, max_hp: n } : { hp: Math.min(n, data.hp.max) } })}
                />
                {' / '}
                <NumberCell
                  value={data.hp.max}
                  label={t('studio.peek.hpMax')}
                  onCommit={(n) => send({ type: 'sheet:update', sheetId, changes: data.hp.current > n ? { max_hp: n, hp: n } : { max_hp: n } })}
                />
              </div>
            ) : (
              <div className="mt-0.5 font-mono text-body tabular-nums text-fg">{value}</div>
            )}
          </div>
        ))}
      </div>

      {/* T-19: управление листом — владелец, группа, публичные поля, удаление (за столом лист только смотрят) */}
      {manage !== undefined && send !== undefined ? (
        <SheetManageBlock sheet={sheet as CharacterSheet} manage={manage} send={send} t={t} />
      ) : null}

      {/* Характеристики: мини-грид 6 */}
      <div className="grid grid-cols-6 gap-1.5">
        {STAT_ORDER.map((stat) => (
          <div
            key={stat}
            className="rounded-md border border-line bg-panel-2 px-2.5 py-2 text-center"
          >
            <div className="text-label font-semibold uppercase tracking-label text-dim">
              {t(STAT_KEY[stat])}
            </div>
            <div className="font-mono text-body-s tabular-nums text-fg">
              {data.stats[stat]}
            </div>
            <div className="text-label tabular-nums text-dim">{fmtMod(data.stats[stat])}</div>
          </div>
        ))}
      </div>

      {/* Черты */}
      {features.length > 0 ? (
        <section>
          <SectionHeader as="h4" className="pane-section-head flex items-center" data-section-head="">
            {t('studio.sheet.features')}
          </SectionHeader>
          <div className="flex flex-col gap-1.5">
            {features.map((f) => (
              <div
                key={f.id}
                className="rounded-md border border-line bg-panel-2 px-2.5 py-2 text-meta text-muted-foreground"
              >
                <span className="font-semibold text-fg">{f.name}.</span>{' '}
                <span className="line-clamp-2">{f.description}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Инвентарь */}
      {items.length > 0 ? (
        <section>
          <SectionHeader as="h4" className="pane-section-head flex items-center" data-section-head="">
            {t('studio.sheet.inventory')}
          </SectionHeader>
          <div className="flex flex-col">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between border-b border-line/60 px-1 py-1 text-meta last:border-b-0"
              >
                <span className="truncate text-fg">{item.name}</span>
                <span className="ml-2 shrink-0 font-mono tabular-nums text-dim">
                  ×{item.quantity}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between px-1 pt-1.5 text-meta">
              <span className="text-dim">{t('studio.sheet.gold')}</span>
              <span className="font-mono tabular-nums text-hl/90">{data.currency.gp} gp</span>
            </div>
          </div>
        </section>
      ) : null}

      {/* Ghost-по-месту (FR-05): pending-черновики этого листа с diff'ами —
          что именно AI предлагает поменять, прямо на карточке. */}
      {hasSheetDraft ? (
        <section className="sheet-entity-view__drafts rounded-md border border-dashed border-hl/50 bg-hl/5 px-3 py-2">
          <h4 className="text-label font-semibold uppercase tracking-label mb-1.5 text-hl">
            {t('studio.sheet.pendingDrafts')}
          </h4>
          <div className="flex flex-col gap-1.5">
            {sheetDrafts.slice(0, 4).map((draft) => {
              const d = draft as { id?: string; toolName?: string; diff?: string };
              return (
                <div key={d.id ?? d.toolName} className="text-meta text-fg">
                  <span className="font-mono text-label text-dim">{d.toolName}</span>
                  <pre className="mt-0.5 whitespace-pre-wrap font-mono text-pill leading-snug text-hl/90">
                    {d.diff ?? ''}
                  </pre>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Упоминается в — переходы к записям, привязанным к листу */}
      {backlinks.length > 0 ? (
        <section className="sheet-entity-view__backlinks border-t border-line/60 pt-3">
          <SectionHeader as="h4" className="pane-section-head flex items-center" data-section-head="">
            {t('studio.sheet.mentionedIn')}
          </SectionHeader>
          <div className="flex flex-wrap gap-1.5">
            {backlinks.map((link) => (
              <button
                key={link.id}
                type="button"
                className="sheet-entity-view__backlink rounded-full border border-line bg-panel-2 px-2.5 py-0.5 text-pill text-fg hover:border-hl/50 hover:text-fg"
                onClick={(): void => onOpenBacklink?.(link.id)}
              >
                📄 {link.name}
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default SheetEntityView;
